import { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import { useAxios } from "../../Helpers/AxiosHelper";
import { ApiResponse, Countries_EXT, DDTreeModel } from "../../Models/Classes";
import { Dropdown, DropdownChangeEvent, ProgressSpinner, ReactCountryFlag } from "../../Common/Imports";

interface DDCountriesProps {
    value?: number;
    onChange?: (value: number | undefined) => void;
    onBlur?: () => void;
    valid?: boolean | undefined;
    showClear?: boolean;
    filter?: boolean;
}

const CountryFlag = ({ countryCode, label }: { countryCode?: string; label?: string }) => {
    const isoCode = countryCode?.trim().toUpperCase();

    return isoCode ? (
        <span className="country-select__flag" aria-hidden="true">
            <ReactCountryFlag
                countryCode={isoCode}
                svg
                title={label}
            />
        </span>
    ) : (
        <span className="country-select__flag country-select__flag--empty" aria-hidden="true">
            <i className="pi pi-globe" />
        </span>
    );
};

interface CountryOption extends DDTreeModel<Countries_EXT> {
    depth: number;
    parentLabel?: string;
}

const flattenCountries = (
    nodes: DDTreeModel<Countries_EXT>[],
    depth = 0,
    parentLabel?: string
): CountryOption[] =>
    nodes.flatMap(node => [
        { ...node, key: Number(node.key), depth, parentLabel },
        ...flattenCountries(node.children ?? [], depth + 1, node.label)
    ]);

export const DDCountries: React.FC<DDCountriesProps> = ({ value, onChange, onBlur, valid, showClear = false, filter = false }) => {
    const [data, setData] = useState<DDTreeModel<Countries_EXT>[] | undefined>();
    const AxiosHelper = useAxios();
    const options = useMemo(() => flattenCountries(data ?? []), [data]);

    const GetData = useCallback(async () => {
        try {
            const resp = await AxiosHelper.get<ApiResponse<DDTreeModel<Countries_EXT>[]>>(`DropDown/Countries`);
            if (resp.data.code === 0) {
                setData(resp.data.data);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    }, [AxiosHelper]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const handleChange = (e: DropdownChangeEvent) => {
        onChange?.(e.value === null || e.value === undefined ? undefined : Number(e.value));
    };

    const countryTemplate = (option: CountryOption) => (
        <span
            className="country-select__option"
            style={{ "--country-depth": option.depth } as CSSProperties}
        >
            {option.depth > 0 && (
                <i className="pi pi-angle-right country-select__child-marker" aria-hidden="true" />
            )}
            <CountryFlag countryCode={option.icon} label={option.label} />
            <span className="country-select__copy">
                <span className="country-select__name">{option.label}</span>
                {option.parentLabel && (
                    <span className="country-select__parent">↳ {option.parentLabel}</span>
                )}
            </span>
            {option.icon && <span className="country-select__code">{option.icon.toUpperCase()}</span>}
        </span>
    );

    const valueTemplate = (option: CountryOption | undefined) =>
        option ? (
            <span className="country-select__value">
                <CountryFlag countryCode={option.icon} label={option.label} />
                <span className="country-select__copy">
                    <span className="country-select__name">{option.label}</span>
                    {option.parentLabel && (
                        <span className="country-select__parent">↳ {option.parentLabel}</span>
                    )}
                </span>
            </span>
        ) : (
            <span className="country-select__placeholder">Select country</span>
        );

    return (

        data ?
            <Dropdown
                value={value}
                onChange={handleChange}
                onBlur={onBlur}
                options={options}
                invalid={valid}
                filter={filter}
                filterBy="label,icon,parentLabel"
                showClear={showClear}
                optionLabel="label"
                optionValue="key"
                itemTemplate={countryTemplate}
                valueTemplate={valueTemplate}
                className="country-select w-100"
                panelClassName="country-select-panel"
                placeholder="Select country"
                filterPlaceholder="Search by country or ISO code"
                emptyFilterMessage="No countries found"
                scrollHeight="20rem"
            />
            :
            <ProgressSpinner style={{ width: '35px', height: '35px' }} />
    );
};
